import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { UserProfile, Gelanggang, Role } from '../../types';
import { getMembersDB, saveMemberDB, saveInvoiceDB, getInvoicesDB, deleteInvoicesBulkDB } from '../../services/db';
import { sanitizeIc, sanitizePhone, findDuplicateIcMember, findDuplicatePhoneMember, toTitleCase } from '../../utils/formatters';
import { createOnboardingInvoice, UNIFORM_SIZES, getUniformPriceBySize } from '../../utils/pricing';
import { SEO } from '../../components/common/SEO';
import {
  User,
  CreditCard,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ShieldAlert,
  LogOut,
  Sparkles,
  Users,
  Compass,
  ArrowRight,
  ShieldCheck,
  Save,
  Ruler
} from 'lucide-react';
import { UniformSizeChartModal } from '../../components/common/UniformSizeChartModal';

export const CompleteProfilePage: React.FC = () => {
  const { currentUser, setCurrentUser, logout } = useAuth();
  const { showToast } = useToast();

  const [name, setName] = useState(
    currentUser?.name && currentUser.name !== 'Pesilat ASMM' ? currentUser.name : ''
  );
  const [icNo, setIcNo] = useState(currentUser?.icNo || '');
  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [address, setAddress] = useState(currentUser?.address || '');
  const [selectedRole, setSelectedRole] = useState<'Ahli' | 'Waris'>(
    currentUser?.role === 'Waris' ? 'Waris' : 'Ahli'
  );
  const [gelanggang, setGelanggang] = useState<Gelanggang>(
    currentUser?.gelanggang && currentUser.gelanggang !== 'Semua Gelanggang'
      ? currentUser.gelanggang
      : 'Pusaka Warisan'
  );
  const [uniformSize, setUniformSize] = useState<string>(currentUser?.uniformSize || '150cm');
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    const cleanName = toTitleCase(name.trim());
    const cleanIc = sanitizeIc(icNo);
    const cleanPhone = sanitizePhone(phone);
    const cleanAddress = address.trim();

    if (!cleanName || cleanName.length < 2) {
      showToast('error', 'Nama Diperlukan', 'Sila masukkan nama penuh seperti dalam MyKad / MyKid.');
      return;
    }

    if (!cleanIc || cleanIc.length < 6) {
      showToast('error', 'No. IC Tidak Sah', 'Sila masukkan No. Kad Pengenalan yang sah (contoh: 030819-02-5819).');
      return;
    }

    if (!cleanPhone || cleanPhone.length < 8) {
      showToast('error', 'No. Telefon Diperlukan', 'Sila masukkan No. Telefon WhatsApp yang sah (contoh: 019-4920194).');
      return;
    }

    if (!cleanAddress || cleanAddress.length < 5) {
      showToast('error', 'Alamat Diperlukan', 'Sila masukkan alamat penuh kediaman anda.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Check for duplicate IC and Phone in existing members
      const existingMembers = await getMembersDB();
      const duplicateIc = findDuplicateIcMember(cleanIc, currentUser.id, existingMembers);
      if (duplicateIc) {
        showToast('error', 'No. IC Telah Berdaftar', `No. Kad Pengenalan ${icNo} sudah didaftarkan dalam sistem atas nama ${duplicateIc.name}.`);
        setIsSubmitting(false);
        return;
      }

      const duplicatePhone = findDuplicatePhoneMember(cleanPhone, currentUser.id, existingMembers);
      if (duplicatePhone) {
        showToast('error', 'No. Telefon Telah Digunakan', `No. Telefon ${phone} sudah didaftarkan dalam sistem atas nama ${duplicatePhone.name}. Setiap ahli perlu menggunakan nombor telefon yang berbeza.`);
        setIsSubmitting(false);
        return;
      }

      const isWaris = selectedRole === 'Waris';
      const assignedRole: Role = isWaris
        ? 'Waris'
        : (currentUser.role && currentUser.role !== 'Waris' ? currentUser.role : 'Ahli');

      const isOld = !isWaris ? Boolean(currentUser.isOldMember) : false;

      const updatedUser: UserProfile = {
        ...currentUser,
        name: cleanName,
        icNo: cleanIc,
        phone: cleanPhone,
        address: cleanAddress,
        role: assignedRole,
        roles: isWaris ? ['Waris'] : [assignedRole],
        gelanggang: isWaris ? ('' as any) : gelanggang,
        sessionTime: isWaris ? '' : 'Semua Sesi',
        bengkung: isWaris ? ('' as any) : (currentUser.bengkung || 'Putih 0 (Pesilat Asas)'),
        bengkungBadge: isWaris ? '' : (currentUser.bengkungBadge || '/badges/putih0.png'),
        status: isWaris ? 'Aktif' : (currentUser.status || 'Aktif'),
        isOldMember: isOld,
        onboardingStatus: isWaris || isOld ? 'completed' : 'pending_payment',
        uniformSize: isWaris ? '' : uniformSize
      };

      // Generate and save onboarding invoice ONLY for new Ahli (pesilat), NEVER for pure Waris
      if (assignedRole === 'Ahli' && !isOld) {
        const onbInv = createOnboardingInvoice(updatedUser, uniformSize);
        await saveInvoiceDB(onbInv);
        updatedUser.onboardingInvoiceId = onbInv.id;
      }

      // If Waris, purge any erroneous auto-generated monthly invoices that might have been created for this ID
      if (isWaris) {
        try {
          const allInvs = await getInvoicesDB();
          const warisMonthlyInvs = allInvs.filter(i => i.memberId === currentUser.id && i.category === 'Yuran Bulanan');
          if (warisMonthlyInvs.length > 0) {
            await deleteInvoicesBulkDB(warisMonthlyInvs.map(i => ({ id: i.id, invoiceNo: i.invoiceNo })));
          }
        } catch {}
      }

      await saveMemberDB(updatedUser);
      setCurrentUser(updatedUser);
      window.dispatchEvent(new Event('asmm_db_updated'));

      showToast('success', 'Profil Berjaya Dilengkapkan!', 'Maklumat peribadi anda telah disimpan.');
    } catch {
      showToast('error', 'Ralat Penyimpanan', 'Gagal mengemaskini profil. Sila cuba lagi sebentar.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col justify-between py-8 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Lengkapkan Profil Ahli | PSSASMM"
        description="Lengkapkan butiran rasmi pendaftaran anda untuk mengaktifkan akaun portal rasmi Persatuan Seni Silat Awang Sulung Merah Muda."
      />

      <div className="max-w-2xl w-full mx-auto space-y-6 animate-fade-in">
        {/* Top Header Card */}
        <div className="bg-slate-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
          <div className="absolute right-0 top-0 translate-x-8 -translate-y-8 w-44 h-44 bg-asmm-red/30 rounded-full blur-3xl pointer-events-none" />

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
            <div className="flex items-center gap-4">
              <img
                src={currentUser?.photoUrl || '/default-avatar.svg'}
                alt={currentUser?.name}
                onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover border-2 border-white/20 shadow-md shrink-0"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider bg-amber-400 text-slate-950 rounded-md">
                    Pengaktifan Akaun
                  </span>
                  <span className="text-xs text-slate-300 font-mono truncate">
                    {currentUser?.email}
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white leading-tight">
                  Lengkapkan Maklumat Peribadi
                </h1>
                <p className="text-xs text-slate-300 mt-1">
                  Sila isi maklumat wajib di bawah sebelum mula menggunakan portal.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={logout}
              className="px-3.5 py-2 bg-white/10 hover:bg-rose-600/30 text-slate-200 hover:text-white border border-white/10 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 self-start sm:self-center cursor-pointer shrink-0"
              title="Log keluar dan tukar akaun"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Tukar Akaun</span>
            </button>
          </div>
        </div>

        {/* Security & Official ROS Notice */}
        <div className="bg-amber-50 border border-amber-200/90 rounded-2xl p-4 flex items-start gap-3 text-xs text-amber-900 shadow-2xs">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <span className="font-extrabold block">Pengesahan Identiti Rasmi PSSASMM (ROS Kedah)</span>
            <p className="text-amber-800 text-[11px] leading-relaxed">
              No. Kad Pengenalan dan Alamat Kediaman adalah mandatori bagi pendaftaran rekod pesilat, perlindungan kebajikan, dan penjanaan resit rasmi yuran.
            </p>
          </div>
        </div>

        {/* Main Onboarding Form Card */}
        <div className="bg-white rounded-3xl border border-slate-200 shadow-md p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Category / Role Selector */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-2">
                Pilih Kategori Keahlian Anda *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedRole('Ahli')}
                  className={`p-4 rounded-2xl border text-left transition-all flex items-center gap-3 cursor-pointer ${selectedRole === 'Ahli'
                    ? 'bg-red-50/70 border-asmm-red text-slate-900 shadow-xs ring-2 ring-asmm-red/30'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                >
                  <div className={`p-2.5 rounded-xl shrink-0 ${selectedRole === 'Ahli' ? 'bg-asmm-red text-white' : 'bg-slate-200 text-slate-600'}`}>
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs font-extrabold text-slate-900">Pesilat / Ahli</div>
                    <div className="text-[11px] text-slate-500">Menyertai kelas latihan silat</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedRole('Waris')}
                  className={`p-4 rounded-2xl border text-left transition-all flex items-center gap-3 cursor-pointer ${selectedRole === 'Waris'
                    ? 'bg-red-50/70 border-asmm-red text-slate-900 shadow-xs ring-2 ring-asmm-red/30'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                >
                  <div className={`p-2.5 rounded-xl shrink-0 ${selectedRole === 'Waris' ? 'bg-asmm-red text-white' : 'bg-slate-200 text-slate-600'}`}>
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs font-extrabold text-slate-900">Waris / Penjaga</div>
                    <div className="text-[11px] text-slate-500">Daftar anak & urus yuran keluarga</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Nama Penuh */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-asmm-red" />
                <span>Nama Penuh (Seperti dalam Kad Pengenalan) *</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Contoh: Muhammad Hafiz Bin Halim"
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold shadow-2xs"
              />
            </div>

            {/* No. IC & No. Telefon */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5 text-asmm-red" />
                  <span>No. Kad Pengenalan *</span>
                </label>
                <input
                  type="text"
                  required
                  value={icNo}
                  onChange={(e) => setIcNo(e.target.value)}
                  placeholder="Contoh: 030819-02-5819"
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-mono font-bold shadow-2xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-emerald-600" />
                  <span>No. Telefon (WhatsApp) *</span>
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Contoh: 019-4920194"
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold shadow-2xs"
                />
              </div>
            </div>

            {/* Alamat Kediaman */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-asmm-red" />
                <span>Alamat Kediaman (Surat-Menyurat) *</span>
              </label>
              <textarea
                rows={3}
                required
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Contoh: No 12, Jalan Sejahtera 3, Taman Sejahtera, 09600 Lunas, Kedah"
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium resize-none shadow-2xs"
              />
            </div>

            {/* Gelanggang (If Pesilat / Ahli) */}
            {selectedRole === 'Ahli' && (
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2.5">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                    <Compass className="w-3.5 h-3.5 text-asmm-red" />
                    <span>Gelanggang Latihan Dipilih *</span>
                  </label>
                  <select
                    value={gelanggang}
                    onChange={(e) => setGelanggang(e.target.value as Gelanggang)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:outline-none text-xs text-slate-900 font-bold"
                  >
                    <option value="Pusaka Warisan">Pusaka Warisan</option>
                    <option value="Wadi AnNour">Wadi AnNour</option>
                    <option value="Paya Besar">Paya Besar</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <span>Saiz Uniform Silat (Ketinggian) *</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsSizeChartOpen(true)}
                      className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1 text-xs font-bold cursor-pointer bg-transparent border-none p-0"
                      title="Buka Carta Cadangan Saiz"
                    >
                      <Ruler className="w-3.5 h-3.5" />
                      <span>Carta Saiz</span>
                    </button>
                  </div>
                  <select
                    value={uniformSize}
                    onChange={(e) => setUniformSize(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:outline-none text-xs text-slate-900 font-bold"
                  >
                    {UNIFORM_SIZES.map(s => (
                      <option key={s} value={s}>
                        {s} — RM {getUniformPriceBySize(s)} ({parseInt(s) <= 130 ? 'Kanak-kanak' : parseInt(s) <= 170 ? 'Remaja / Dewasa' : 'Tinggi / Dewasa'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 bg-asmm-red hover:bg-asmm-crimson disabled:opacity-50 text-white font-extrabold text-sm rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer border-none active:scale-[0.99]"
              >
                {isSubmitting ? (
                  <span>Menyimpan Maklumat...</span>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Simpan</span>
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Footer Branding */}
      <footer className="mt-8 text-center text-xs text-slate-500">
        <span>© 2026 PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA (PPM-007-02-28082022)</span>
      </footer>

      {/* Uniform Size Chart Modal Popup */}
      <UniformSizeChartModal
        isOpen={isSizeChartOpen}
        onClose={() => setIsSizeChartOpen(false)}
        selectedSize={uniformSize}
        onSelectSize={(s) => setUniformSize(s)}
      />
    </div>
  );
};
